import { h, Fragment } from "preact";
import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "preact/hooks";
import { omit, throttle } from "lodash";

import { AccessibilityMenu } from "./_implementation/AccessibilityMenu";
import { CaptionFetcher } from "./_implementation/CaptionFetcher";
import {
  accessibilityOptionsReducer,
  AccessibilityOptionsState,
  defaultAccessibilityOptionsState,
  TranscriptType,
} from "./_implementation/accessibilityOptionsReducer";
import { ClientAttributes } from "./_implementation/getClientAttributes";
import { getTargetHeight } from "./_implementation/getTargetHeight";
import { AccessibilityMenuUrlParser } from "./_implementation/AccessibilityMenuUrlParser";

/**
 * The root component
 */
export function App({
  lineCount,
  liveAsrUniqueKey,
  summarizationKey,
  showAccessibilityMenu,
  showCompleteTranscript,
  pollAfterEvent,
  transcriptContainerHeight,
  useUppercase,
}: ClientAttributes) {
  const [showScrollBottomButton, setShowScrollBottomButton] = useState(false);
  const [
    scrollToBottomOnTranscriptChange,
    setScrollToBottomOnTranscriptChange,
  ] = useState(true);
  const [message, setMessage] = useState("");
  const [transcript, setTranscript] = useState("");

  // These options need to match exactly the values in the OPTIONS
  // constants in AccessibilityMenu.tsx, or their selections will appear empty
  // on page load.
  // TODO: Enforce the behavior described above.
  const initialAccessibilityOptionsState: AccessibilityOptionsState =
    defaultAccessibilityOptionsState;

  const [accessibilityOptionsState, accessibilityOptionsDispatch] = useReducer(
    accessibilityOptionsReducer,
    initialAccessibilityOptionsState,
  );

  const targetEl = useRef<HTMLDivElement>(null);

  const CAPTION_KEY_MAP: Record<TranscriptType, string | undefined> = {
    verbatim: liveAsrUniqueKey,
    summarized: summarizationKey,
  };
  const captionsKey = CAPTION_KEY_MAP[accessibilityOptionsState.transcriptType];

  /**
   * Whether or not the transcript should include character count based paragraphs
   */
  const insertParagraphs = showCompleteTranscript && !lineCount;

  // The component singleton pattern below guarantees an imperative class instance
  // only gets created once
  // https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
  const captionFetcher = useRef<CaptionFetcher | null>(null);
  function getCaptionFetcher() {
    if (captionFetcher.current === null) {
      captionFetcher.current = new CaptionFetcher({
        insertParagraphs,
        captionsKey,
        setMessage,
        setTranscript,
        pollAfterEvent,
        useUppercase,
      });
    }
    return captionFetcher.current;
  }

  function setAccessibilityOptions() {
    accessibilityOptionsDispatch({
      type: "SET_ACCESSIBILITY_OPTIONS",
      newValue: new AccessibilityMenuUrlParser().parse(),
    });
  }

  useEffect(() => {
    setAccessibilityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start or update the CaptionFetcher
  useEffect(() => {
    getCaptionFetcher().start(captionsKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsKey]);

  function handleScrollBottomClick() {
    if (targetEl.current)
      targetEl.current.scrollTop = targetEl.current.scrollHeight;
  }

  const handleTargetScroll = throttle(() => {
    /** Whether or not the user is scrolled to the bottom with a 10px buffer */
    const scrolledToBottom =
      targetEl.current !== null &&
      targetEl.current.scrollHeight - (targetEl.current.scrollTop + 10) <=
        targetEl.current.clientHeight;
    setShowScrollBottomButton(!scrolledToBottom);
    if (showCompleteTranscript)
      setScrollToBottomOnTranscriptChange(scrolledToBottom);
  }, 100);

  // Scroll to the bottom of the caption on transcript change if
  // scrollToBottomOnTranscriptChange is true
  useLayoutEffect(() => {
    if (targetEl.current !== null && scrollToBottomOnTranscriptChange) {
      targetEl.current.scrollTop = targetEl.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  /**
   * The height of the transcript container
   */
  const targetHeight = getTargetHeight({
    fontSize: parseInt(
      accessibilityOptionsState.fontSize.replace("px", ""),
      10,
    ),
    lineCount,
    showCompleteTranscript,
    transcriptContainerHeight,
  });

  /**
   * The classNames for the transcript container
   */
  const classNames = [
    // Remove paragraph margins when displaying based off line count
    ["remove-paragraph-margins", !insertParagraphs],
    // Display scroll bar when displaying the complete transcript
    ["show-scroll", showCompleteTranscript],
  ]
    .filter(([, shouldDisplay]) => shouldDisplay)
    .map(([name]) => name)
    .join(" ");

  const accessibilityStyles = omit(accessibilityOptionsState, [
    "displayMode",
    "showTranscriptType",
    "transcriptType",
  ]);

  useEffect(() => {
    const clientContainerElement = document.getElementById("live-asr-client");
    if (clientContainerElement) {
      clientContainerElement.style.color = accessibilityStyles.color;
      clientContainerElement.style.backgroundColor =
        accessibilityStyles.backgroundColor;
    }
  }, [accessibilityStyles.color, accessibilityStyles.backgroundColor]);

  return (
    <>
      {showAccessibilityMenu && (
        <AccessibilityMenu
          accessibilityOptionsDispatch={accessibilityOptionsDispatch}
          accessibilityOptionsState={accessibilityOptionsState}
        />
      )}
      {showCompleteTranscript && (
        <div id="live-asr-button-container">
          <button
            className={`btn btn-primary btn-sm ${
              showScrollBottomButton ? "" : "d-none"
            }`}
            onClick={handleScrollBottomClick}
          >
            Scroll to bottom
          </button>
        </div>
      )}
      <div
        id="live-asr-target"
        data-testid="live-asr-target"
        ref={targetEl}
        onScroll={handleTargetScroll}
        className={classNames}
        style={{ height: targetHeight, ...accessibilityStyles }}
        // This is being done for performance. See CaptionFetcher for more details.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: transcript }}
      />
      <div id="live-asr-message">{message}</div>
    </>
  );
}
